import { DocumentIcon, MicrophoneIcon, PaperClipIcon, PhotoIcon, VideoCameraIcon } from '@heroicons/react/24/outline';

import type { Message } from '../../pages/cardex/api/useMessage';

import { t } from '../i18n/langue';

/**
 * Les pièces jointes d'un message, REÇU comme ENVOYÉ — audit UI/UX du 2026-09-23,
 * P0 n°3. `sortant` ne change que les couleurs de la puce, posée sur une bulle de
 * marque (audit 2026-09-23).
 *
 * #16 (l.66) : « puce cliquable dans la bulle, ouverture par le navigateur ». Pas
 * d'aperçu en ligne : une puce par pièce, nommée par son type (ou son nom de
 * fichier pour un e-mail). En standalone iOS, `target="_blank"` ouvre une feuille
 * Safari par-dessus l'app ; on revient en la fermant.
 *
 * Les URL arrivent absolues, construites par le serveur (`App\Support\MediaUrl`) :
 * une adresse étrangère y est déjà écartée. Ne jamais accepter ici des URL
 * absolues arbitraires : `urlSure` reste la défense en profondeur.
 *
 * Le libellé est une CLÉ (`messagerie.piecesJointes.*`), traduite au rendu : une
 * chaîne posée au chargement du module resterait dans la langue du démarrage.
 */
const TYPES = {
    image: { cle: 'photo', Icone: PhotoIcon },
    audio: { cle: 'noteVocale', Icone: MicrophoneIcon },
    video: { cle: 'video', Icone: VideoCameraIcon },
    document: { cle: 'document', Icone: DocumentIcon },
} as const;

/**
 * Défense en profondeur (relecture M8) : un `href` venu du serveur n'est rendu que
 * s'il est en `https:` ou servi par la même origine. `javascript:`, `data:` ou un
 * `http:` étranger ne deviennent jamais une puce.
 */
export function urlSure(url: string | null | undefined): url is string {
    if (!url) return false;
    try {
        const u = new URL(url, window.location.href);
        return u.protocol === 'https:' || u.origin === window.location.origin;
    } catch {
        return false;
    }
}

export function aDesPiecesJointes(message: Message): boolean {
    return urlSure(message.media_url) || (message.attachments ?? []).some((f) => urlSure(f.download_url));
}

interface PiecesJointesProps {
    message: Message;
    sortant: boolean;
}

export default function PiecesJointes({ message, sortant }: PiecesJointesProps) {
    if (!aDesPiecesJointes(message)) return null;

    const puce = `inline-flex min-h-touch max-w-full items-center gap-1.5 rounded-xl px-3 font-semibold ${
        sortant ? 'bg-main-color-dark text-white' : 'bg-main-color-light text-main-color-dark'
    }`;
    const type = message.media_type && message.media_type in TYPES ? TYPES[message.media_type as keyof typeof TYPES] : null;
    const Icone = type?.Icone ?? PaperClipIcon;

    return (
        <div className="mb-1 flex flex-wrap gap-1.5">
            {urlSure(message.media_url) && (
                <a href={message.media_url} target="_blank" rel="noopener noreferrer" className={puce}>
                    <Icone aria-hidden="true" className="h-4 w-4 shrink-0" />
                    {t(`messagerie.piecesJointes.${type?.cle ?? 'autre'}`)}
                </a>
            )}
            {(message.attachments ?? []).filter((f) => urlSure(f.download_url)).map((f) => (
                <a key={f.index} href={f.download_url} target="_blank" rel="noopener noreferrer" className={puce}>
                    <PaperClipIcon aria-hidden="true" className="h-4 w-4 shrink-0" />
                    <span className="truncate">{f.filename}</span>
                </a>
            ))}
        </div>
    );
}
